import { useState, useEffect } from "react";
import PencilIcon from "@/assets/icons/EditPencilOrange.svg";
import { CustomButton } from "@/components/ui/Buttons";
import { 
  useGetAvailabilityByCreatorIdQuery, 
  useToggleProfileVisibilityMutation 
} from "@/redux/feature/sameDayDelivery/sameDayDeliveryApi";
import { useGetShippingAddressesQuery } from "@/redux/feature/shippingAddress/shippingAddressApi";
import { useGlobalContext } from "@/context/GlobalContext";
import toast from "react-hot-toast";
import { AlertCircle, Eye, EyeOff } from "lucide-react";

export default function SameDayAvailabilityCard({
  setShowModifySlot,
}: {
  setShowModifySlot: () => void;
}) {
  const { userData } = useGlobalContext();
  const [showWarning, setShowWarning] = useState(false);

  // Fetch current same-day availability data
  const { data: availabilityData, refetch: refetchAvailability } = useGetAvailabilityByCreatorIdQuery(
    undefined,
    { skip: !userData?.creator_id }
  );

  // Fetch addresses to validate selection
  const { data: addressesData } = useGetShippingAddressesQuery(
    encodeURIComponent(userData?.email || ""),
    { skip: !userData?.email }
  );

  const [toggleVisibility, { isLoading: isToggling }] = useToggleProfileVisibilityMutation();

  const availability = availabilityData?.data;
  const addresses = addressesData?.data || [];
  const hasValidAddresses = addresses.length > 0;

  // Check if selected address is still valid
  const selectedAddressExists = availability?.address_id 
    ? addresses.some((addr: any) => addr._id === availability.address_id)
    : false;

  const isProfileVisible = availability?.isProfileVisible ?? false;
  const canToggleOn = hasValidAddresses && (availability?.address_id ? selectedAddressExists : true);

  const handleToggleVisibility = async () => {
    if (!canToggleOn && !isProfileVisible) {
      setShowWarning(true);
      return;
    }

    try {
      await toggleVisibility({ isProfileVisible: !isProfileVisible }).unwrap();
      toast.success(`Profile ${!isProfileVisible ? 'enabled' : 'disabled'} successfully`);
      refetchAvailability();
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to update profile visibility');
    }
  };

  const getVisibilityStatus = () => {
    if (!availability) return { text: "Not Set Up", color: "text-gray-500" };
    if (!canToggleOn) return { text: "Address Required", color: "text-red-600" };
    if (isProfileVisible) return { text: "Visible to Clients", color: "text-green-600" };
    return { text: "Hidden from Clients", color: "text-gray-600" };
  };

  const status = getVisibilityStatus();
  return (
    <div className="w-full h-full">
      <div className="w-full h-full rounded-xl border border-[#E6E6E6] px-6 py-6 flex flex-col gap-4 same-day-banner-gradient">
        
        {/* Header with Toggle */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div className="flex flex-col gap-2 max-w-[520px]">
            <div className="flex items-center gap-3">
              <h3 className="text-[17px] font-semibold text-[#1A1A1A] leading-[1.4]">
                Available for <span className="text-[#FF6600]">Same-Day</span>{" "}
                Projects?
              </h3>
              
              {/* Toggle Switch */}
              {availability && (
                <button
                  onClick={handleToggleVisibility}
                  disabled={isToggling || (!canToggleOn && !isProfileVisible)}
                  className={`
                    relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                    ${isProfileVisible && canToggleOn 
                      ? 'bg-green-600' 
                      : 'bg-gray-300'
                    }
                    ${isToggling ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    ${!canToggleOn && !isProfileVisible ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  <span
                    className={`
                      inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                      ${isProfileVisible && canToggleOn ? 'translate-x-6' : 'translate-x-1'}
                    `}
                  />
                </button>
              )}
            </div>

            {/* Status Text */}
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium ${status.color}`}>
                Status: {status.text}
              </span>
              {isProfileVisible && canToggleOn && (
                <Eye className="w-3 h-3 text-green-600" />
              )}
              {!isProfileVisible && (
                <EyeOff className="w-3 h-3 text-gray-600" />
              )}
            </div>

            <p className="text-[13px] text-[#9CA3AF] leading-[1.6]">
              Mark yourself available for single-day delivery to receive urgent
              projects and get paid today. You'll have 3–4 hours to complete each
              project.
            </p>
          </div>

          {/* Action Button */}
          <div className="shrink-0 w-full lg:w-auto">
            <CustomButton
              backgroundColor="#FFFFFF"
              color="#344054"
              fontSize="13px"
              fontWeight="500"
              onClick={setShowModifySlot}
              padding="9px 18px"
              borderRadius="20px"
              border="1px solid #E5E5E5"
              className="flex items-center gap-x-1.5 w-full lg:w-auto justify-center whitespace-nowrap"
            >
              <PencilIcon />
              Modify Slots
            </CustomButton>
          </div>
        </div>

        {/* Warning Message */}
        {showWarning && (
          <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-amber-800 dark:text-amber-200 font-medium">
                  Address Required
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  {!hasValidAddresses 
                    ? "Please add at least one address to enable profile visibility."
                    : "Your selected address has been deleted. Please select a valid address to enable visibility."
                  }
                </p>
                <button
                  onClick={() => setShowWarning(false)}
                  className="text-xs text-amber-800 dark:text-amber-200 underline mt-1 hover:no-underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Address Issue Warning (Auto-displayed) */}
        {availability && !canToggleOn && (
          <div className="p-3 rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/20">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-red-800 dark:text-red-200 font-medium">
                  Profile Visibility Disabled
                </p>
                <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                  {!hasValidAddresses 
                    ? "No addresses available. Add an address to enable profile visibility."
                    : "Your selected address has been deleted. Update your address selection to restore visibility."
                  }
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
